import { Component, input } from '@angular/core';
import { JunoError } from '../juno-error/juno-error';
import { FormValueClassAbstract } from '../../../abstract/form-value-class-abstract';
import { TranslocoDirective } from '@jsverse/transloco';

/**
 * A styled, accessible multi-line textarea built on top of
 * {@link FormValueClassAbstract} and integrated with Angular Signal Forms.
 *
 * The component wraps a native `<textarea>` element, forwards all Signal Forms
 * state (validation errors, touched, disabled …) automatically through the
 * abstract base class, and renders inline validation messages via
 * `<juno-error>`.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * | Input    | Type                                             | Default      | Description                                                              |
 * |----------|--------------------------------------------------|--------------|--------------------------------------------------------------------------|
 * | `rows`   | `number`                                         | `4`          | Number of visible text rows; maps directly to the native `rows` attribute. |
 * | `resize` | `'none' \| 'vertical' \| 'horizontal' \| 'both'` | `'vertical'` | CSS `resize` direction for the textarea.                                 |
 *
 * ### Inputs (inherited from {@link FormValueClassAbstract})
 *
 * `value`, `label`, `labelClass`, `placeholder`, `name`, `appliedClasses`,
 * `required`, `pattern`, `errors`, `pending`, `touched`, `dirty`, `disabled`.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted on blur so Signal Forms marks the field as touched.
 *
 * ---
 *
 * @usageNotes
 *
 * #### With Signal Forms (`[formField]`)
 * ```html
 * <juno-libs-ui-textarea
 *   [formField]="form.notes"
 *   label="GENERAL.NOTES"
 *   placeholder="Enter notes…"
 * />
 *
 * <juno-libs-ui-textarea
 *   [formField]="form.description"
 *   label="GENERAL.DESCRIPTION"
 *   [rows]="6"
 *   resize="none"
 * />
 * ```
 *
 * #### Standalone two-way binding (`[(value)]`)
 * ```html
 * <juno-libs-ui-textarea
 *   [(value)]="commentSignal"
 *   label="GENERAL.COMMENT"
 *   name="comment"
 *   placeholder="Write a comment…"
 * />
 * ```
 *
 * @see {@link FormValueClassAbstract} for the full list of inherited inputs/outputs.
 * @see {@link JunoInput} for the single-line text-input counterpart.
 */
@Component({
  selector: 'juno-libs-ui-textarea',
  imports: [JunoError, TranslocoDirective],
  templateUrl: './juno-textarea.html',
  styleUrl: './juno-textarea.css',
})
export class JunoTextarea extends FormValueClassAbstract<string, string> {
  rows = input<number>(4);
  resize = input<'none' | 'vertical' | 'horizontal' | 'both'>('vertical');
}
